import React, { useState, useEffect } from 'react';
import { styled } from '@mui/system';
import { Box, Card, CardContent, CardMedia, Divider, IconButton, Link, Modal, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLocationDot, faCodeBranch, faCirclePlay, faGaugeHigh } from '@fortawesome/free-solid-svg-icons';
import { faAndroid, faApple } from '@fortawesome/free-brands-svg-icons';
import Chip from '@mui/material/Chip';
import { PiEngineFill } from "react-icons/pi";
import { ReactComponent as GasPumpIcon } from '../../icons/GasPump_IconOnly.svg';
import { ReactComponent as GasPumpElectricIcon } from '../../icons/GasPump_Electric_IconOnly.svg';
import { ReactComponent as BatteryChargingIcon } from '../../icons/BatteryCharging_IconOnly.svg';
import { ReactComponent as DriveTrainIcon } from '../../icons/Drivetrain_IconOnly.svg';
import { ReactComponent as Stars } from '../../icons/Stars.svg';

import InventoryGateway from '../../gateway/inventory/InventoryGateway';

const StyledCard = styled(Card)(({ theme }) => ({
  maxWidth: 345,
  margin: theme.spacing(2),
  textAlign: 'center',
  padding: theme.spacing(2),
  boxShadow: '0px 0px 10px 0px rgb(0, 0, 0, 0.3)',
}));

const InventoryPreview = ({config, messages, inventory, setInventory}) => {
    const [open, setOpen] = useState(false);
    const [videoUrl, setVideoUrl] = useState('');
    const [sharedMessages, setSharedMessages] = useState([]);

    const handleOpen = (url) => {
        setVideoUrl(url);
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
        setVideoUrl('');
    };

    useEffect(() => {
        const handleEscape = (event) => {
          if (event.key === 'Escape') {
            handleClose();
          }
        };
    
        document.addEventListener('keydown', handleEscape);
        return () => {
          document.removeEventListener('keydown', handleEscape);
        };
    }, []);

    useEffect(() => {
        const fetchInventory = async (preparedMessages) => {
            try {
                const gateway = new InventoryGateway({config});
                const vehicles = await gateway.locate(preparedMessages);
        
              setInventory(vehicles);
            } catch (error) {
              console.error('Error fetching vehicles:', error);
            }
        };

        if (sharedMessages.length > 0) {
            setInventory(fetchInventory(sharedMessages));
        }
    }, [config, setInventory, sharedMessages]);

    useEffect(() => {
        if (messages !== sharedMessages) {
            setSharedMessages(messages);
        }
    }, [messages, sharedMessages]);

    useEffect(() => {
        const fetchIntelligentPitch = async () => {
            try {
                const gateway = new InventoryGateway({config});
                if (inventory && inventory.length > 0) {
                    const promises = inventory[0].map(vehicle => 
                        vehicle.pitch ? Promise.resolve(vehicle.pitch) : gateway.intelligentPitch(vehicle.VIN)
                    );
                    const responses = await Promise.all(promises);
                    const updatedVehicles = inventory[0].map((vehicle, index) => ({
                        ...vehicle,
                        pitch: vehicle.pitch || responses[index]
                    }));
                    inventory[0] = updatedVehicles;
                    setInventory(inventory);
                }
            } catch (error) {
                console.error('Error fetching intelligent pitch:', error);
            }
        };

        fetchIntelligentPitch();
    }, [config, inventory, setInventory]);


    const placeholderUrl = function (url) {
        try {
            new URL(url);
            return url;
        } catch (e) {
            return './placeholder.jpg';
        }
    };

    // const abbreviateDriveTrain = (driveTrain) => {
    //     switch (driveTrain) {
    //         case 'All Wheel Drive':
    //             return 'AWD';
    //         case 'Front Wheel Drive':
    //             return 'FWD';
    //         case 'Rear Wheel Drive':
    //             return 'RWD';
    //         case 'Four Wheel Drive':
    //             return '4WD';
    //         default:
    //             return driveTrain;
    //     }
    // };

    const hasAppleCarPlay = (vehicle) => {
        return ((vehicle.StandardEquipment 
            && vehicle.StandardEquipment.indexOf('Apple') > -1) 
            || 
            (vehicle.OptionalEquipment 
                && vehicle.OptionalEquipment.indexOf('Apple') > -1));
    }

    const hasAndroidAuto = (vehicle) => {
        return ((vehicle.StandardEquipment 
            && vehicle.StandardEquipment.indexOf('Android') > -1) 
            || 
            (vehicle.OptionalEquipment 
                && vehicle.OptionalEquipment.indexOf('Android') > -1));
    }
    
    const decode = (pitch) => {
        return pitch.replace(/&quot;/g, '"').replace(/&lt;/g, '<').replace(/&gt;/g, '>');
    };

    const cleanse = (pitch) => {
        return decode(pitch.replace(/["<>]/g, ''));
    };

    return (
        <Box display="flex" flexWrap="wrap" justifyContent="center">
        {inventory && inventory.length > 0 && inventory[0].map((vehicle) => (
            <StyledCard key={vehicle.VIN}>
                <Typography variant="body2" color="text.secondary" textAlign={'left'}>{vehicle.VIN}</Typography>
                <CardMedia
                    component="img"
                    alt={`${vehicle.Make} ${vehicle.Model}`}
                    height="140"
                    image={placeholderUrl(vehicle.PhotoURL)}
                    title={`${vehicle.Make} ${vehicle.Model}`}
                />
                <CardContent>
                    <Typography variant="body2" size="small" textAlign={'left'}>{vehicle.VehicleCondition}</Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'row', marginTop: '2px', textAlign: 'left'}} >
                        <Box sx={{ marginRight: 'auto'}}>
                            <Typography variant="body2" style={{ textWrap:'nowrap', fontWeight: 'bold' }}>
                                {vehicle.Year} {vehicle.Make} {vehicle.Model}
                            </Typography>
                            <Typography variant='body2' >
                                {vehicle.Trim} {vehicle.BodyType}
                            </Typography>
                        </Box>
                        <Box textAlign={'right'} >
                            <Typography variant='body2' fontWeight={'bold'}>
                                ${vehicle.SellingPrice ? vehicle.SellingPrice.toLocaleString() : ""}
                            </Typography>
                            {/* <Typography display={'flex'}>
                                MSRP:
                                <Typography sx={ {textDecoration: 'line-through'}}>${vehicle.ListPrice ? vehicle.ListPrice.toLocaleString() : ""}</Typography>
                            </Typography> */}
                        </Box>
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: 'row', marginTop: '2px', textAlign: 'left'}} >
                        <Typography variant="h7" color="text.secondary" textAlign={'left'} alignContent={'left'}>
                            <FontAwesomeIcon 
                                icon={faLocationDot} 
                                style={{ 
                                    marginRight: 5, 
                                    color: 'rgb(207, 32, 47)', 
                                    verticalAlign: 'top', 
                                    height: '20px',
                                }} />
                                <Link href={vehicle.VDP} target="_blank" rel="noopener" color="inherit">
                                    {vehicle.DealerName}
                                </Link>
                        </Typography>
                    </Box>
                    {vehicle.pitch && (
                        <Box sx={{ display: 'flex', flexDirection: 'row', marginTop: '10px', textAlign: 'left', borderStyle:"solid", borderRadius:'4px', borderWidth:'2px', background:'rgb(254, 246, 246)', borderColor: 'rgb(207, 32, 47)' }} >
                            <Box sx={{ display: 'flex', flexDirection: 'row', marginLeft: '10px', marginTop: '10px', textAlign: 'left'}} >
                                <Stars
                                    style={{
                                        marginLeft: 5,
                                        marginRight: 7,
                                        color: 'rgb(207, 32, 47)', 
                                        verticalAlign: 'top', 
                                        height: '30px',
                                        width: '30px',
                                        marginTop: 'none'
                                    }}
                                />
                            </Box>
                            <Typography variant="body1" color="lightslategray">
                                {cleanse(vehicle.pitch)}
                            </Typography>
                            <Box sx={{ display: 'flex', flexDirection: 'row', marginTop: '10px', textAlign: 'left'}} >
                                <Stars
                                    style={{
                                        marginRight: 5,
                                        marginBottom: 7,
                                        color: 'rgb(207, 32, 47)', 
                                        verticalAlign: 'top', 
                                        height: '30px',
                                        width: '30px',
                                        marginTop: 'auto'
                                    }}
                                />
                            </Box>
                        </Box>
                    )}
                    <Divider style={{marginTop: 10, marginBottom: 10}} />
                    <Box sx={{ display: 'flex', flexDirection: 'row', marginTop: '2px', textAlign: 'left'}} >
                        <Typography variant="body2" color="text.secondary">
                            <PiEngineFill 
                                style={{ color: 'rgb(207, 32, 47)', height: '20px', verticalAlign: 'middle' }} /> {vehicle.Engine}
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: 'row', marginTop: '2px', textAlign: 'left'}} >
                        <Typography variant="body2" color="text.secondary">
                            <FontAwesomeIcon 
                                icon={faCodeBranch}
                                style={{ 
                                    marginRight: 5, 
                                    color: 'rgb(207, 32, 47)', 
                                    verticalAlign: 'top', 
                                    height: '20px'
                                }} /> {vehicle.Transmission}
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: 'row', marginTop: '2px', textAlign: 'left'}} >
                        {vehicle.FuelType && (
                            <Box sx={{ display: 'flex', flexDirection: 'row', marginTop: '2px', marginRight: 2, textAlign: 'left'}} >
                                {(vehicle.FuelType !== 'Electric' && !vehicle.FuelType.includes('Hybrid')) && (
                                <GasPumpIcon
                                    style={{ 
                                        marginRight: 5,
                                        paddingTop: '2px', 
                                        color: 'rgb(207, 32, 47)', 
                                        verticalAlign: 'top', 
                                        height: '15px',
                                        width: '15px'
                                    }} />
                                )}
                                {vehicle.FuelType.includes('Hybrid') && (
                                <GasPumpElectricIcon 
                                    style={{ 
                                        marginRight: 5, 
                                        paddingTop: '2px',
                                        color: 'rgb(207, 32, 47)', 
                                        verticalAlign: 'top', 
                                        height: '15px',
                                        width: '15px'
                                    }} />
                                )}
                                {(vehicle.FuelType.includes('Electric') && !vehicle.FuelType.includes('Hybrid')) && (        
                                <BatteryChargingIcon
                                    style={{ 
                                        marginRight: 5, 
                                        paddingTop: '2px',
                                        color: 'rgb(207, 32, 47)', 
                                        verticalAlign: 'top', 
                                        height: '15px',
                                        width: '15px' 
                                    }} />
                                )}
                                <Typography variant="body2" color="text.secondary">
                                    {vehicle.FuelType}
                                </Typography>
                            </Box>
                        )}
                        {( (vehicle.CityMPG && vehicle.CityMPG > 0) || (vehicle.HighwayMPG && vehicle.HighwayMPG > 0) ) && (  
                            <Box sx={{ display: 'flex', flexDirection: 'row', marginTop: '2px', textAlign: 'left'}} >
                                <FontAwesomeIcon 
                                    icon={faGaugeHigh} 
                                    style={{ 
                                        marginRight: 5, 
                                        color: 'rgb(207, 32, 47)', 
                                        verticalAlign: 'top', 
                                        height: '20px'
                                    }} />        
                                <Typography variant="body2" color="text.secondary">
                                    {
                                        vehicle.CityMPG ? (vehicle.CityMPG + (vehicle.HighwayMPG ? ' / ' + vehicle.HighwayMPG : '') ) : vehicle.HighwayMPG
                                    }
                                </Typography>
                            </Box>
                        )}
                    </Box>
                    {(vehicle.DriveTrain || vehicle.Wheelbase) && (
                        <Box sx={{ display: 'flex', flexDirection: 'row', marginTop: '2px', textAlign: 'left'}} >
                            <DriveTrainIcon 
                                style={{ 
                                    marginRight: 5, 
                                    paddingTop: '2px',
                                    color: 'rgb(207, 32, 47)', 
                                    verticalAlign: 'top', 
                                    height: '15px',
                                    width: '15px',
                                    marginLeft: '-3px'
                                }} />
                            <Typography variant="body2" color="text.secondary">
                                {vehicle.DriveTrain}
                            </Typography>
                        </Box>
                    )}
                    <Box sx={{ display: 'flex', flexDirection: 'row', marginTop: '2px', textAlign: 'left'}} >
                            <Box sx={{ display: 'flex', flexDirection: 'row', textAlign: 'left'}} >
                                {hasAppleCarPlay(vehicle) && (
                                <Box sx={{ display: 'flex', flexDirection: 'row', marginRight: 2, textAlign: 'left'}} >
                                    <FontAwesomeIcon 
                                        icon={faApple} 
                                        style={{ 
                                            marginRight: 5, 
                                            color: 'rgb(207, 32, 47)', 
                                            verticalAlign: 'top', 
                                            height: '20px'
                                        }} />
                                    <Typography variant="body2" color="text.secondary">
                                        Apple CarPlay
                                    </Typography>
                                </Box>
                                )}
                                {hasAndroidAuto(vehicle) && (
                                <Box sx={{ display: 'flex', flexDirection: 'row', textAlign: 'left'}} >
                                    <FontAwesomeIcon 
                                        icon={faAndroid} 
                                        style={{ 
                                            marginRight: 5, 
                                            color: 'rgb(207, 32, 47)', 
                                            verticalAlign: 'top', 
                                            height: '20px'
                                        }} />
                                    <Typography variant="body2" color="text.secondary">
                                        Android Auto
                                    </Typography>
                                </Box>
                             )}
                            </Box>
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: 'row', marginTop: '10px', flexWrap: 'wrap'}} >
                        {vehicle.StandardEquipment && vehicle.StandardEquipment.map((equipment, index) => (
                            <Chip 
                                key={'std_equipment_' + index} 
                                label={equipment} 
                                size="small" 
                                variant="filled" 
                                style={{margin: 2, maxWidth: 150, fontSize: '.61em', fontWeight: 'bold', color: 'rgb(255,255,255)', backgroundColor: 'rgb(207,32,47)' }} 
                            />
                        ))}
                        {vehicle.OptionalEquipment && vehicle.OptionalEquipment.map((equipment, index) => (
                            <Chip 
                                key={'opt_equipment_' + index} 
                                label={equipment} 
                                size="small" 
                                variant="filled" 
                                style={{margin: 2, maxWidth: 150, fontSize: '.61em', fontWeight: 'bold', color: 'rgb(255,255,255)', backgroundColor: 'rgb(65, 59, 59)' }} 
                            />
                        ))}
                    </Box>
                    {vehicle.VideoURL && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 140 }}>
                        <FontAwesomeIcon
                            icon={faCirclePlay}
                            size="3x"
                            color="rgb(207, 32, 47)"
                            onClick={() => handleOpen(vehicle.VideoURL)}
                            style={{ cursor: 'pointer', marginRight: 5 }}
                        />
                        <Typography variant="body2" color="text.secondary" textAlign={'left'}>
                            Watch Video
                        </Typography>
                    </Box>
                    )}
                </CardContent>
            </StyledCard>
        ))}
            <Modal
                open={open}
                onClose={handleClose}
                aria-labelledby="video-modal-title"
                aria-describedby="video-modal-description"
                >
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                    <IconButton onClick={handleClose} style={{ position: 'absolute', top: 10, right: 10 }}>
                        <CloseIcon sx={{ color: '#0071eb' }} />
                    </IconButton>
                    <iframe
                        width="100%"
                        height="100%"
                        src={videoUrl}
                        title="Vehicle Video"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                        style={{ border: 0 }}
                    ></iframe>
                </Box>
            </Modal>
        </Box>
    );
};

export default InventoryPreview;